import { usePathname, useRouter } from "next/navigation";
import { useState } from "react";

import { getDepictMarketFromPricelistId } from "@/app/products/[...category]/serverUtils/helpers";
import { DepictOptions } from "@/lib/depict/types";
import { useBoolean } from "@/lib/hooks/useBoolean";
import { useDebounce } from "@/lib/hooks/useDebounce";
import useLocation from "@/lib/hooks/useLocation";
import { getDepictProducts } from "@/lib/hooks/useProductFilter/getProducts";
import { keepPreviousData, useQuery } from "@tanstack/react-query";

const useGlobalSearch = () => {
  const { value: open, toggle, setValue } = useBoolean(false);
  const [search, setSearch] = useState<string>("");
  const location = useLocation();

  const debouncedSearch = useDebounce<string>(search, 300);
  const options: DepictOptions = {
    query: debouncedSearch,
    limit: 12,
    merchant: "camillapihl",
    locale: "en",
    market: getDepictMarketFromPricelistId(String(location?.pricelist)),
  };

  const { data, isLoading } = useQuery({
    queryKey: ["search", debouncedSearch],
    queryFn: () => getDepictProducts(options, null),
    enabled: !!debouncedSearch && debouncedSearch.length >= 2,
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
  });

  const toggleOpen = () => {
    toggle();
    if (!open) {
      setSearch("");
    }
  };

  const router = useRouter();
  const paths = usePathname()
    .split("/")
    .filter((x) => x.length > 0)
    .splice(1);

  const navigateToPage = () => {
    const categoryPath =
      paths?.[0] === "shop" || paths?.[0] === "products"
        ? paths.length > 0
          ? `/${paths.map((path) => path).join("/")}`
          : ""
        : "";
    const uri = "/products" + categoryPath + "?search=" + search;
    toggleOpen();
    router.push(uri);
    setSearch("");
  };

  const clearSearch = () => {
    setSearch("");
  };

  const setOpen = (open: boolean) => {
    setValue(open);
    if (!open) {
      setSearch("");
    }
  };

  return {
    open,
    toggleOpen,
    search,
    isLoading,
    setSearch,
    navigateToPage,
    clearSearch,
    data,
    setOpen,
  };
};

export default useGlobalSearch;
